import axios from 'axios'
import { captureError, SENTRY_SEVERITY, toApiError, toServerError } from 'src/utils/sentry'
import CmdType from 'src/service/constants'
import { ERR_CODES } from 'src/types/errcode'
import { ResponseHeader } from 'src/types/type'

/** 모니터링 대상 CmdType (전체) */
export const WATCH_CMD_TYPES = new Set<string>(Object.values(CmdType))

/** 정상 실패로 간주하는 ErrCode */
export const KNOWN_ERR_CODES = new Set<string>(Object.values(ERR_CODES))

/**
 * HTTP 200 응답의 알 수 없는 ErrCode를 ApiError로 남기는 함수
 *
 * watch CmdType이 아니거나, ErrCode 목록에 있는 known 실패면 수집하지 않는다.
 * 서버가 ErrMsg를 내려준 응답은 화면 alert로 처리되므로 Issue로 남기지 않는다.
 * ErrMsg가 비어 있는 알 수 없는 ErrCode만 수집한다.
 * ErrorBoundary / HTTP 5xx와 역할을 섞지 않는다.
 */
export function captureUnexpectedErrCode(header?: ResponseHeader) {
  if (!header) return
  const { ErrCode, CmdType: cmdType, ErrMsg } = header

  if (!cmdType || !WATCH_CMD_TYPES.has(cmdType)) return
  if (!ErrCode || KNOWN_ERR_CODES.has(ErrCode)) return
  if (ErrMsg?.trim()) return

  captureError(
    toApiError(
      new Error(`[API] Unexpected error | cmdType=${cmdTypeLabel(cmdType)} errCode=${ErrCode}`),
    ),
    {
      level: SENTRY_SEVERITY.Warning,
      tags: { cmdType: String(cmdType), errCode: String(ErrCode) },
      fingerprint: ['api-unexpected-error', String(cmdType), String(ErrCode)],
    },
  )
}

const HTTP_STATUS_LABEL: Record<number, string> = {
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
}

/**
 * axios HTTP 5xx만 ServerError로 남기는 함수
 *
 * offline / ERR_NETWORK / abort / 4xx는 제외한다.
 */
export function captureServerError(error: unknown, cmdType: string) {
  if (!axios.isAxiosError(error)) return

  const status = error.response?.status
  if (!status || status < 500 || status >= 600) return

  const statusLabel = HTTP_STATUS_LABEL[status] ?? 'Server Error'

  captureError(toServerError(new Error(`[Server] HTTP ${status} ${statusLabel}`)), {
    level: SENTRY_SEVERITY.Error,
    tags: {
      httpStatus: String(status),
      cmdType: String(cmdType),
    },
    extra: {
      method: error.config?.method,
      response: error.response?.data,
    },
    fingerprint: ['server-error', String(cmdType), String(status)],
  })
}

/**
 * CmdType 코드를 Sentry 표기용 라벨로 변환한다.
 *
 * @example
 * cmdTypeLabel('010002') // "sendotpverfy (010002)"
 * cmdTypeLabel('999999') // "999999"
 */
export function cmdTypeLabel(cmdType: string): string {
  for (const [name, value] of Object.entries(CmdType)) {
    if (value === cmdType) return `${name} (${cmdType})`
  }
  return cmdType
}
